import { formatRegionLabel, normalizeRegion } from "../constants/regions.js";
import { getTierInfoForRegion, resolveEquipmentIncluded } from "../constants/regionTiers.js";

// Igual que STAGES_BLOCKING_DATE (EventModal.js) y EMAIL_STAGES_BLOCKING_DATE
// (Emailing.js): la señal pendiente ocupa el día igual que confirmado/realizado.
const RESERVED_STAGES = new Set(["confirmed", "done", "accepted_pending_deposit"]);

const STAGE_WEIGHT = {
  done: 1,
  confirmed: 0.96,
  accepted_pending_deposit: 0.82,
  negotiation: 0.66,
  proposal_sent: 0.56,
  meeting: 0.48,
  contacted: 0.4,
  info_needed: 0.32,
  lead: 0.24,
};

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function money(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function estimateFallbackCost(region) {
  const normalized = normalizeRegion(region);
  const tier = getTierInfoForRegion(normalized);
  if (!tier) return 900;

  const byTier = {
    1: 450,
    2: 650,
    3: 950,
    4: 1450,
    5: 1250,
    6: 2100,
  };
  return byTier[tier.tier] || 900;
}

function getProximityScore(region) {
  const normalized = normalizeRegion(region);
  const tier = getTierInfoForRegion(normalized);
  if (!normalized || !tier) return 8;

  if (normalized === "cantabria") return 25;
  if (["gipuzkoa", "araba", "navarra", "la rioja"].includes(normalized)) return 24;
  if (normalized === "bizkaia") return 23;
  if (["asturias", "burgos", "palencia", "valladolid", "leon"].includes(normalized)) return 19;
  if (["madrid", "avila", "segovia", "guadalajara", "toledo"].includes(normalized)) return 18;
  if (["barcelona", "girona", "lleida", "tarragona"].includes(normalized)) return 7;

  const byTier = { 1: 23, 2: 22, 3: 17, 4: 11, 5: 18, 6: 5 };
  return byTier[tier.tier] || 8;
}

function getOperationalRisk(region) {
  const normalized = normalizeRegion(region);
  const tier = getTierInfoForRegion(normalized);
  if (!tier) return { score: 5, label: "Riesgo por zona sin tramo" };
  if (normalized === "cantabria") return { score: 10, label: "Ida y vuelta probable" };
  if (tier.tier <= 2) return { score: 9, label: "Logistica cercana" };
  if (tier.tier === 3 || tier.tier === 5) return { score: 7, label: "Desplazamiento asumible" };
  if (tier.tier === 4) return { score: 4, label: "Lejos, posible noche fuera" };
  return { score: 2, label: "Muy lejos / alto desgaste" };
}

function getTypeScore(type) {
  if (type === "boda") return 10;
  if (type === "empresa") return 7;
  if (type === "privado") return 4;
  return 5;
}

export function enrichEventPriority(event, { currentTeamId = null, isCandidate = false } = {}) {
  const region = normalizeRegion(event?.region) || null;
  const tier = getTierInfoForRegion(region);
  const amount = money(event?.amount);
  const forecastExpense = money(event?.forecast_expense || event?.forecast_total);
  const equipmentIncluded = resolveEquipmentIncluded(event, tier);
  const providersCost = equipmentIncluded ? money(tier?.providersPrice) : 0;
  const technicianCost = equipmentIncluded ? money(tier?.technicianCost) : 0;
  const estimatedCost = (forecastExpense || estimateFallbackCost(region)) + providersCost + technicianCost;
  const net = amount ? amount - estimatedCost : 0;

  const marginScore = amount
    ? clamp((net / 7000) * 35, 0, 35)
    : clamp(((tier?.basePrice || 0) - estimatedCost) / 7000 * 22, 0, 22);
  const proximityScore = getProximityScore(region);
  const certaintyScore = clamp((STAGE_WEIGHT[event?.stage] ?? 0.35) * 15, 0, 15);
  const typeScore = getTypeScore(event?.type);
  const operational = getOperationalRisk(region);
  const sameTeamBonus = currentTeamId && event?.team_id === currentTeamId ? 5 : 0;
  const score = Math.round(marginScore + proximityScore + certaintyScore + typeScore + operational.score + sameTeamBonus);

  const reasons = [];
  if (amount) reasons.push(`Margen estimado ${Math.round(net).toLocaleString("es-ES")} EUR`);
  else reasons.push("Sin precio cerrado");
  reasons.push(formatRegionLabel(region) || "Zona sin definir");
  reasons.push(operational.label);
  if (RESERVED_STAGES.has(event?.stage)) reasons.push("Fecha ya reservada");
  if (sameTeamBonus) reasons.push("Misma banda/equipo");

  return {
    ...event,
    _priority: {
      score,
      amount,
      estimatedCost,
      net,
      regionLabel: formatRegionLabel(region) || "Sin zona",
      tierLabel: tier ? `Tramo ${tier.tier}` : "Sin tramo",
      isCandidate,
      isReserved: RESERVED_STAGES.has(event?.stage),
      reasons,
    },
  };
}

export function rankEventConflicts(events, options = {}) {
  return (events || [])
    .map((event) => enrichEventPriority(event, options))
    .sort((a, b) => (b._priority?.score || 0) - (a._priority?.score || 0));
}
